// src/pages/Inventory/GoodsReceiptNoteConfirm_EntryGST.jsx
// ✅ Converted from: GoodsReceiptNoteConfirm_EntryGST.aspx / .aspx.cs + Inv_GRNConfirmEntry.cs
// ✅ Uses Node routes: /api/inventory/grn-confirm-entry/*
// ✅ NO rights logic (excluded as instructed)
// ✅ All business logic preserved: FIFO check, item merging, calculation, VAT approval
// ✅ Pagination added for both grids
// ✅ Insert + Update modes — BOTH fully working
// ✅ File upload (invoice documents)
// ✅ FIXED: sessionStorage race condition (useMemo → useEffect + useState)
// ✅ FIXED: date format dd/MM/yyyy → yyyy-MM-dd for HTML date inputs
// ✅ FIXED: items loaded even when PO rows empty (uses grnConfHeader directly)
// ✅ FIXED: grnConfHeader used as single source of truth for update mode fields

import { useEffect, useContext, useState, useRef, useCallback } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import LayoutWrapper from "../../components/LayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ArrowLeft, Save, RefreshCcw, Upload, FileText, Trash2,
  ChevronLeft, ChevronRight, AlertCircle, CheckCircle2,
} from "lucide-react";

const BASE_URL = "http://nissi-india.com";

// ─────────────────────── helpers ───────────────────────
const getTodayISO = () => new Date().toISOString().slice(0, 10);

const getNowTime = () => {
  const d = new Date();
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")} ${ampm}`;
};

const roundTwo = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * Convert any date string → yyyy-MM-dd (required by <input type="date">)
 * Handles: dd/MM/yyyy, dd-MM-yyyy, yyyy-MM-dd, ISO strings
 */
const toISODate = (v) => {
  if (!v) return "";
  const s = String(v).trim();
  // Already yyyy-MM-dd or ISO
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  // dd/MM/yyyy  or  dd-MM-yyyy
  const sep = s.includes("/") ? "/" : "-";
  const parts = s.split(sep);
  if (parts.length === 3 && parts[0].length <= 2) {
    return `${parts[2].slice(0, 4)}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
  }
  return s;
};

/** Merge items with same itemid + same tax rates — weighted avg price */
function mergeItems(items) {
  const merged = [];
  for (const item of items) {
    const existing = merged.find(
      (m) =>
        m.fld_itemid === item.fld_itemid &&
        m.fld_VAT    === item.fld_VAT &&
        m.fld_CST    === item.fld_CST &&
        m.fld_ED     === item.fld_ED &&
        m.fld_DS     === item.fld_DS &&
        m.fld_SC     === item.fld_SC &&
        m.fld_PandF  === item.fld_PandF
    );
    if (existing) {
      const totalGrnQty = parseFloat(existing.fld_grnqty) + parseFloat(item.fld_grnqty);
      const price =
        parseFloat(existing.fld_grnqty) * parseFloat(existing.fld_poprice) +
        parseFloat(item.fld_grnqty)     * parseFloat(item.fld_poprice);
      existing.fld_POqty       = parseFloat(existing.fld_POqty)       + parseFloat(item.fld_POqty);
      existing.fld_grnqty      = totalGrnQty;
      existing.fld_acceptedqty = parseFloat(existing.fld_acceptedqty) + parseFloat(item.fld_acceptedqty);
      existing.fld_poprice     = totalGrnQty > 0 ? roundTwo(price / totalGrnQty) : 0;
      existing._Ref_POId       = (existing._Ref_POId || "") + "," + item.fld_purchaseorderid;
    } else {
      merged.push({ ...item, _Ref_POId: String(item.fld_purchaseorderid) });
    }
  }
  return merged;
}

/** Calculate totals from items list */
function calculateTotals(items, charges) {
  let totit = 0, sumds = 0, subtotal = 0, calvat = 0, calcst = 0, caled = 0, calgst = 0, calamt = 0;
  const unitPrices = {};

  for (const item of items) {
    if (!item._checked) continue;
    const qty   = parseFloat(item.fld_acceptedqty) || 0;
    const price = parseFloat(item.fld_poprice)     || 0;
    const ds    = parseFloat(item.fld_DS)           || 0;
    const vat   = parseFloat(item.fld_VAT)          || 0;
    const cst   = parseFloat(item.fld_CST)          || 0;
    const ed    = parseFloat(item.fld_ED)            || 0;

    let total  = roundTwo(price * qty);
    let distot = total;
    totit += total;
    const discAmt = roundTwo(distot * ds / 100);
    sumds  += discAmt;
    distot -= discAmt;
    subtotal += distot;

    const _caled  = roundTwo(distot * ed  / 100);
    const _calvat = roundTwo(distot * vat / 100);
    const _calcst = roundTwo(distot * cst / 100);
    caled  += _caled;
    calvat += _calvat;
    calcst += _calcst;
    calamt += distot + _calvat + _calcst + _caled;
    calgst += _calvat + _calcst + _caled;

    unitPrices[`${item.fld_itemid}_${item.fld_VAT}_${item.fld_CST}_${item.fld_ED}`] =
      qty > 0 ? roundTwo(distot / qty) : 0;
  }

  const frieght   = parseFloat(charges?.fld_frieght)      || 0;
  const insurance = parseFloat(charges?.fld_insurance)     || 0;
  const tpf       = parseFloat(charges?.fld_pf)            || 0;
  const other     = parseFloat(charges?.fld_othercharges)  || 0;
  const tcsamt    = parseFloat(charges?.fld_tcsamt)        || 0;
  const tcsper    = parseFloat(charges?.fld_tcspercent)    || 0;

  let sg = 0, cg = 0, ig = 0;
  if (charges?.fld_frttax === 1) {
    sg += (parseFloat(charges.fld_frtsgst) * frieght)   / 100;
    cg += (parseFloat(charges.fld_frtcgst) * frieght)   / 100;
    ig += (parseFloat(charges.fld_frtigst) * frieght)   / 100;
  }
  if (charges?.fld_instax === 1) {
    sg += (parseFloat(charges.fld_inssgst) * insurance) / 100;
    cg += (parseFloat(charges.fld_inscgst) * insurance) / 100;
    ig += (parseFloat(charges.fld_insigst) * insurance) / 100;
  }
  if (charges?.fld_pftax === 1) {
    sg += (parseFloat(charges.fld_pfsgst)  * tpf)       / 100;
    cg += (parseFloat(charges.fld_pfcgst)  * tpf)       / 100;
    ig += (parseFloat(charges.fld_pfigst)  * tpf)       / 100;
  }

  const rawTotal = calamt + other + insurance + tpf + tcsamt + frieght;
  const rem = rawTotal % 1;
  let roundOff = rem >= 0.5 ? roundTwo(1 - rem) : roundTwo(-rem);
  if (rem === 0) roundOff = 0;

  const totalAmt = Math.round(calamt + other + insurance + tpf + frieght + roundOff + tcsamt + cg + sg + ig);

  return {
    subtotal:   roundTwo(totit),
    discount:   roundTwo(sumds),
    afDisTotal: roundTwo(subtotal),
    taxValue:   roundTwo(calgst),
    frieght, insurance, pf: tpf, other,
    cgst:    roundTwo(calcst + cg),
    sgst:    roundTwo(calvat + sg),
    igst:    roundTwo(caled  + ig),
    tcsamt, tcsper,
    roundOff,
    totalAmt,
    unitPrices,
  };
}

// ─────────────────────── Pagination ───────────────────────
const Pagination = ({ page, totalPages, totalCount, pageSize, onPageChange, isDark, primaryBtnClass }) => {
  const from = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const to   = Math.min(page * pageSize, totalCount);
  if (totalPages <= 1 && totalCount === 0) return null;
  return (
    <div className="flex flex-col md:flex-row items-center justify-between mt-3 gap-3 px-2">
      <div className={`text-xs font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
        Showing <b>{from}</b>–<b>{to}</b> of <b>{totalCount}</b>
      </div>
      {totalPages > 1 && (
        <div className={`inline-flex rounded-xl overflow-hidden border ${isDark ? "border-white/10 bg-gray-900" : "border-gray-200 bg-white"}`}>
          <button onClick={() => onPageChange(Math.max(1, page - 1))} disabled={page === 1}
            className={`px-3 py-1.5 text-xs font-bold flex items-center gap-1 ${page === 1 ? "text-gray-400 cursor-not-allowed" : isDark ? "text-gray-200 hover:bg-gray-800" : "text-gray-700 hover:bg-gray-50"}`}>
            <ChevronLeft className="w-3 h-3" /> Prev
          </button>
          {[...Array(Math.min(totalPages, 7))].map((_, i) => {
            const pg = i + 1;
            return (
              <button key={pg} onClick={() => onPageChange(pg)}
                className={`px-3 py-1.5 text-xs font-bold transition ${page === pg ? `${primaryBtnClass} text-white` : isDark ? "text-gray-200 hover:bg-gray-800" : "text-gray-700 hover:bg-gray-50"}`}>
                {pg}
              </button>
            );
          })}
          <button onClick={() => onPageChange(Math.min(totalPages, page + 1))} disabled={page === totalPages}
            className={`px-3 py-1.5 text-xs font-bold flex items-center gap-1 ${page === totalPages ? "text-gray-400 cursor-not-allowed" : isDark ? "text-gray-200 hover:bg-gray-800" : "text-gray-700 hover:bg-gray-50"}`}>
            Next <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════ MAIN COMPONENT ═══════════════════════
const GoodsReceiptNoteConfirmEntryGST = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const primaryBtnClass   = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  // ── localStorage ──
  const branchId    = Number(localStorage.getItem("branchId")              || 0);
  const branchType  =        localStorage.getItem("branchType")            || "HO";
  const loginUserId = Number(localStorage.getItem("userId")                || 0);
  const finYearId   = Number(localStorage.getItem("finYearId")             || 0);
  const finYearStart =       localStorage.getItem("financeyearstartdate")  || "";

  // ── sessionStorage — read via useEffect to avoid navigation race with useMemo ──
  const [payload,       setPayload]       = useState(null);
  const [grnConfHeader, setGrnConfHeader] = useState(null);
  const [isUpdateMode,  setIsUpdateMode]  = useState(false);

  useEffect(() => {
    try {
      const raw  = sessionStorage.getItem("grnConfirmSelectionPayload");
      const upd  = sessionStorage.getItem("grnConfirmUpdateMode");
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed) {
        setGrnConfHeader(parsed.grnConfHeader || null);
        setPayload(parsed);
      }
      setIsUpdateMode(upd ? JSON.parse(upd) : false);
    } catch (e) {
      console.error("sessionStorage parse error:", e);
    }
  }, []);

  // ── style helpers ──
  const inputBase = `w-full px-3 py-2 rounded-xl border outline-none transition text-sm ` +
    (isDark
      ? "bg-gray-800/60 border-white/10 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-400");
  const labelBase = `text-xs font-semibold uppercase tracking-wide ${isDark ? "text-gray-300" : "text-gray-600"}`;
  const cardBase  = `rounded-2xl border shadow-lg overflow-hidden ` +
    (isDark ? "border-white/10 bg-gray-800/40 backdrop-blur-md" : "border-gray-200 bg-white");
  const thBase = `px-3 py-2.5 text-left text-xs font-bold uppercase tracking-wider text-white ${primaryBtnClass}`;
  const tdBase = `px-3 py-2 text-xs ${isDark ? "text-gray-300" : "text-gray-700"}`;

  // ── meta / display state ──
  const [siteInventory,    setSiteInventory]    = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmation,     setConfirmation]     = useState(false);
  const [grnConfNo,        setGrnConfNo]        = useState("");
  const [grnNo,            setGrnNo]            = useState("");
  const [grnInsNo,         setGrnInsNo]         = useState("");
  const [supplier,         setSupplier]         = useState("");
  const [branchMode,       setBranchMode]       = useState("BRANCH");

  // ── editable form fields ──
  const [grnDate,      setGrnDate]      = useState(getTodayISO());
  const [grnTime,      setGrnTime]      = useState(getNowTime());
  const [refDate,      setRefDate]      = useState("");
  const [invoiceNo,    setInvoiceNo]    = useState("");
  const [invoiceDate,  setInvoiceDate]  = useState("");
  const [refNo,        setRefNo]        = useState("");
  const [remarks,      setRemarks]      = useState("");
  const [deliveryType, setDeliveryType] = useState("");
  const [employees,    setEmployees]    = useState([]);
  const [employeeId,   setEmployeeId]   = useState("Select");

  // ── IDs ──
  const [grnConfId,     setGrnConfId]     = useState(null);
  const [grnId,         setGrnId]         = useState(null);
  const [grnInsId,      setGrnInsId]      = useState(null);
  const [maxGrnInsDate, setMaxGrnInsDate] = useState("");

  // ── grids ──
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [allItems,       setAllItems]       = useState([]);
  const [displayItems,   setDisplayItems]   = useState([]);
  const [charges,        setCharges]        = useState(null);
  const [totals,         setTotals]         = useState(null);
  const [infoMessage,    setInfoMessage]    = useState("");

  // ── PO grid pagination ──
  const [poPage, setPoPage] = useState(1);
  const PO_PAGE_SIZE = 10;
  const poTotalPages = Math.ceil(purchaseOrders.length / PO_PAGE_SIZE);
  const poPagedRows  = purchaseOrders.slice((poPage - 1) * PO_PAGE_SIZE, poPage * PO_PAGE_SIZE);

  // ── Items grid pagination ──
  const [itemPage, setItemPage] = useState(1);
  const ITEM_PAGE_SIZE = 10;
  const itemTotalPages = Math.ceil(allItems.length / ITEM_PAGE_SIZE);

  // ── file upload ──
  const [invoiceFiles,    setInvoiceFiles]    = useState([]);
  const [selectedFileIdx, setSelectedFileIdx] = useState(null);
  const fileInputRef = useRef(null);

  // ── loading ──
  const [loading,    setLoading]    = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ─── sync displayItems with allItems + pagination ───
  useEffect(() => {
    const start = (itemPage - 1) * ITEM_PAGE_SIZE;
    setDisplayItems(allItems.slice(start, start + ITEM_PAGE_SIZE));
  }, [allItems, itemPage]);

  // ─── recalc totals ───
  useEffect(() => {
    if (allItems.length > 0 && charges !== null) {
      setTotals(calculateTotals(allItems, charges));
    }
  }, [allItems, charges]);

  // ─── load meta ───
  const loadMeta = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-confirm-entry/meta`, {
        params: { branchId, finYearId },
      });
      if (res.data?.success) {
        setSiteInventory(res.data.siteInventory || false);
        setEmployees(res.data.employees || []);
        setShowConfirmation(res.data.flagConf || false);
      }
    } catch (err) {
      toast.error("Failed to load meta: " + (err?.response?.data?.message || err.message));
    }
  }, [branchId, finYearId]);

  // ─── generate GRN Conf No (add mode only) ───
  const loadGrnConfNo = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-confirm-entry/generate-conf-no`, {
        params: { loginUserId },
      });
      if (res.data?.success) setGrnConfNo(res.data.grnConfNo);
    } catch { /* silent */ }
  }, [loginUserId]);

  // ─── load charges — pass resolvedGrnId directly to avoid stale state ───
  const loadCharges = useCallback(async (purIds, resolvedGrnId) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-confirm-entry/charges`, {
        params: { purIds: purIds.join(","), grnId: resolvedGrnId || 0, branchId },
      });
      if (res.data?.success) setCharges(res.data.charges);
    } catch { /* silent */ }
  }, [branchId]);

  // ─── FIFO check ───
  const runFifoCheck = useCallback(async (items, date) => {
    if (!items.length || !date) return items;
    try {
      const itemIds = [...new Set(items.map((i) => i.fld_itemid))].join(",");
      const storeid = items[0]?.fld_storeid || 0;
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-confirm-entry/fifo-check`, {
        params: { itemIds, branchId, storeid, entryDate: date },
      });
      if (res.data?.success && res.data.futureItems?.length > 0) {
        const futureIds = new Set(res.data.futureItems.map((f) => String(f.fld_itemid)));
        setInfoMessage("UnChecked Items Already Issued on FutureDate. So you cant purchase items before the Issue Date");
        return items.map((item) => ({
          ...item,
          _checked:     !futureIds.has(String(item.fld_itemid)),
          fld_itemupdt:  futureIds.has(String(item.fld_itemid)) ? 0 : 1,
        }));
      } else {
        setInfoMessage("");
        return items.map((i) => ({ ...i, _checked: true, fld_itemupdt: 1 }));
      }
    } catch {
      return items;
    }
  }, [branchId]);

  // ═══════════════════════════════════════════════════════════════
  // ─── MAIN INIT — triggers when payload is loaded from sessionStorage ───
  // ═══════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!payload) return;

    (async () => {
      setLoading(true);
      try {
        await loadMeta();

        const poData = payload.grnconf_purchaseorder || [];
        const hdr    = grnConfHeader; // header built by listing page — most reliable source

        setBranchMode(payload.grn_type || "BRANCH");
        setPurchaseOrders(poData);

        // ── Resolve key IDs ──
        const resolvedGrnInsId = hdr?.fld_grninsid
          || payload.grnInsId
          || poData[0]?.fld_grninsid
          || null;

        const resolvedGrnId = hdr?.fld_grnid
          || poData[0]?.fld_grnid
          || null;

        if (resolvedGrnInsId) setGrnInsId(Number(resolvedGrnInsId));
        if (resolvedGrnId)    setGrnId(Number(resolvedGrnId));

        // ── Display-only read-only fields ──
        setSupplier(hdr?.fld_suppliername    || poData[0]?.fld_suppliername    || "");
        setGrnInsNo(hdr?.fld_Insbranchwiseno || poData[0]?.fld_Insbranchwiseno || "");

        // GRN No — prefer formatted string (contains "/") over raw int
        const rawGrnNo = poData[0]?.fld_grnno || hdr?.fld_grnno || "";
        setGrnNo(String(rawGrnNo).includes("/") ? String(rawGrnNo) : (String(hdr?.fld_grnno || rawGrnNo)));

        // max GRN Inspection date — used for date validation
        const insDateCandidates = poData
          .map((r) => toISODate(r.fld_grninsdate || r.fld_date || ""))
          .filter(Boolean);
        if (insDateCandidates.length) setMaxGrnInsDate([...insDateCandidates].sort().reverse()[0]);

        // ── MODE-SPECIFIC population ──
        if (isUpdateMode && hdr) {
          // ── UPDATE MODE: populate all header fields from grnConfHeader ──
          setGrnConfId(hdr.fld_grnconfid ? Number(hdr.fld_grnconfid) : null);
          setGrnConfNo(hdr.fld_grnconfno    || "");
          setGrnDate(toISODate(hdr.fld_date)    || getTodayISO());
          setGrnTime(hdr.fld_grntime            || getNowTime());
          setRefDate(toISODate(hdr.fld_refdate) || "");
          setRefNo(hdr.fld_refno                || "");
          setRemarks(hdr.fld_remarks            || "");
          setInvoiceNo(hdr.fld_invno            || "");
          setInvoiceDate(toISODate(hdr.fld_invdate) || "");
          setDeliveryType(hdr.fld_deliverytype  || "");
          if (hdr.fld_employeeid) setEmployeeId(String(hdr.fld_employeeid));
        } else {
          // ── ADD MODE: generate new conf no + build refNo from PO list ──
          await loadGrnConfNo();
          const poNos = poData
            .map((r) => r.fld_pono || r.fld_POno || r.fld_branchwiseno)
            .filter(Boolean);
          setRefNo([...new Set(poNos)].join(","));
        }

        // ── Fetch Items ──
        if (resolvedGrnInsId) {
          const itemsRes = await axios.get(`${BASE_URL}/api/inventory/grn-confirm-entry/items`, {
            params: {
              insIds:   String(resolvedGrnInsId),
              isUpdate: isUpdateMode ? "true" : "false",
              finYearId,
            },
          });

          if (itemsRes.data?.success) {
            const raw    = itemsRes.data.items || [];
            const merged = mergeItems(raw);

            let finalItems;
            if (isUpdateMode) {
              // Update mode — use fld_itemupdt flag from DB
              finalItems = merged.map((i) => ({ ...i, _checked: !!i.fld_itemupdt }));
            } else {
              // Add mode — run FIFO date check
              finalItems = await runFifoCheck(merged, grnDate);
            }

            setAllItems(finalItems);

            const purIds = [...new Set(raw.map((i) => i.fld_purchaseorderid).filter(Boolean))];
            await loadCharges(purIds, resolvedGrnId);
          }
        }

      } catch (err) {
        console.error("Init error:", err);
        toast.error("Init error: " + err.message);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  // ─── GRN Date change → re-run FIFO (add mode only) ───
  const handleGrnDateChange = async (newDate) => {
    setGrnDate(newDate);
    if (!newDate) return;

    const today = getTodayISO();
    if (newDate > today) {
      toast.warn("Please select Date Less than or Equal to Current date");
      setGrnDate(""); return;
    }
    const fyStart = toISODate(finYearStart);
    if (fyStart && newDate < fyStart) {
      toast.warn(`You can select only Greater than or Equal to Financial Year Date ${finYearStart}`);
      setGrnDate(fyStart); return;
    }
    if (maxGrnInsDate && newDate < maxGrnInsDate) {
      toast.warn("Select GRN Confirm date greater than or equals GRN Inspection date");
      setGrnDate(""); return;
    }
    if (!isUpdateMode && allItems.length > 0) {
      const updated = await runFifoCheck(allItems, newDate);
      setAllItems(updated);
    }
  };

  // ─── Invoice No blur → duplicate check (add mode only) ───
  const handleInvoiceNoBlur = async () => {
    if (!invoiceNo.trim() || !grnInsNo || isUpdateMode) return;
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-confirm-entry/check-invoice`, {
        params: { invoiceNo: invoiceNo.trim(), grnInsNo, branchId },
      });
      if (res.data?.isDuplicate) {
        toast.warn("Invoice No Already Exists..");
        setInvoiceNo("");
      }
    } catch { /* silent */ }
  };

  // ─── Item checkbox toggle ───
  const toggleItemCheck = (globalIdx, checked) => {
    setAllItems((prev) =>
      prev.map((item, i) =>
        i === globalIdx ? { ...item, _checked: checked, fld_itemupdt: checked ? 1 : 0 } : item
      )
    );
  };

  // ─── File upload ───
  const handleFileAdd = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) { toast.warn("Please select a file first"); return; }
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${BASE_URL}/api/inventory/grn-confirm-entry/upload-invoice`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.success) {
        setInvoiceFiles((prev) => [...prev, res.data.filename]);
        fileInputRef.current.value = "";
        toast.success("File added");
      }
    } catch (err) {
      toast.error("Upload failed: " + err.message);
    }
  };

  // ─── Validation ───
  const validate = () => {
    if (!grnDate)              { toast.warn("Please enter Date"); return false; }
    if (grnDate > getTodayISO()) { toast.warn("Please select Date Less than or Equal to Current date"); return false; }
    if (maxGrnInsDate && grnDate < maxGrnInsDate) { toast.warn("Select GRN Confirm date greater than or equals GRN Inspection date"); return false; }
    if (!invoiceNo.trim())     { toast.warn("Please enter Invoice No"); return false; }
    if (!remarks.trim())       { toast.warn("Please enter Remarks"); return false; }
    if (!invoiceDate)          { toast.warn("Please enter Invoice Date"); return false; }
    return true;
  };

  // ─── Invoice duplication final check (add mode only) ───
  const invoiceValidation = async () => {
    if (!invoiceNo.trim()) { toast.warn("Enter Invoice No"); return false; }
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-confirm-entry/validate-invoice`, {
        params: { invoiceNo: invoiceNo.trim(), supplierName: supplier, grnNo, branchId },
      });
      if (res.data?.isDuplicate) { toast.warn("Invoice No should not be duplicated"); return false; }
    } catch { return false; }
    return true;
  };

  // ─── SUBMIT ───
  const handleSubmit = async () => {
    if (!validate()) return;
    if (!isUpdateMode && !(await invoiceValidation())) return;

    const checkedItems = allItems.filter((i) => i._checked && i.fld_itemupdt === 1);
    if (checkedItems.length === 0) {
      toast.warn("Already Issue Details exist for all items on date so pl select future date");
      return;
    }

    setSubmitting(true);
    try {
      const body = {
        isUpdate:     isUpdateMode,
        grnConfId:    grnConfId || null,
        grnConfNo,
        grnId,
        grnInsId,
        branchId,
        loginUserId,
        grnDate,
        grnTime,
        refDate:      refDate || null,
        refNo,
        remarks:      remarks.replace(/'/g, "''"),
        invoiceNo:    invoiceNo.trim(),
        invoiceDate,
        deliveryType,
        employeeId:   employeeId !== "Select" ? employeeId : null,
        supplierid:
  grnConfHeader?.fld_supplierid
  || payload?.grnConfHeader?.fld_supplierid
  || payload?.grnconf_purchaseorder?.[0]?.fld_supplierid
  || null,
        items: checkedItems.map((item) => ({
          ...item,
          fnl_UnitPrice:    totals?.unitPrices?.[`${item.fld_itemid}_${item.fld_VAT}_${item.fld_CST}_${item.fld_ED}`] || 0,
          fld_IsVatApprove: item._vatApprove !== undefined ? item._vatApprove : 1,
        })),
        invoiceFiles,
      };

      const res = await axios.post(`${BASE_URL}/api/inventory/grn-confirm-entry/submit`, body);

      if (res.data?.success) {
        toast.success(isUpdateMode ? "GRN Confirmation Updated successfully" : "GRN Confirmation Inserted successfully");
        sessionStorage.removeItem("grnConfirmSelectionPayload");
        sessionStorage.removeItem("grnConfirmUpdateMode");
        setTimeout(() => { window.location.href = "/Inventory/GoodsReceiptNoteConfirm_Listing"; }, 1500);
      } else {
        toast.error(res.data?.message || "Transaction Not Inserted. Please refresh and try");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Transaction failed");
    } finally {
      setSubmitting(false);
    }
  };

  // ─────────────────────── RENDER HELPERS ───────────────────────
  const sectionTitle = (txt) => (
    <div className={`text-xs font-bold uppercase tracking-widest mb-3 px-1 ${isDark ? "text-blue-300" : "text-blue-700"}`}>
      {txt}
    </div>
  );

  const summaryRow = (label, value, highlight = false) => (
    <div className={`flex items-center justify-between py-1.5 px-3 rounded-lg ${highlight ? (isDark ? "bg-blue-900/30" : "bg-blue-50") : ""}`}>
      <span className={`text-xs font-semibold ${isDark ? "text-gray-300" : "text-gray-600"}`}>{label}</span>
      <span className={`text-sm font-bold tabular-nums ${highlight ? (isDark ? "text-blue-200" : "text-blue-700") : (isDark ? "text-white" : "text-gray-900")}`}>
        {value ?? "—"}
      </span>
    </div>
  );

  // ─────────────────────── RENDER ───────────────────────
  return (
    <LayoutWrapper>
      <ToastContainer position="top-right" autoClose={2800} theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen p-4 lg:p-6 ${isDark ? "bg-gray-900" : "bg-slate-50"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-2xl tracking-tight">GRN Confirm Entry</span>}
          setSkin={setSkin} currentSkin={skin}
        />

        <div className={`mx-auto max-w-7xl mt-5 rounded-3xl shadow-2xl border p-5 lg:p-7
          ${isDark ? "bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md border-white/10 text-white"
                   : "bg-white border-gray-200 text-gray-900"}`}>

          {/* ── Top bar ── */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
            <div>
              <h1 className={`text-xl font-extrabold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}>
                GRN Confirmation Entry
              </h1>
              <p className={`text-xs mt-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                {isUpdateMode ? "Update Mode" : "New Entry"} &nbsp;•&nbsp; {branchMode}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { window.location.href = "/Inventory/GoodsReceiptNoteConfirm_Listing"; }}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold border transition
                  ${isDark ? "border-white/10 text-white hover:bg-white/10" : "border-gray-200 text-gray-700 hover:bg-gray-50"}`}>
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button onClick={handleSubmit} disabled={submitting || loading}
                className={`inline-flex items-center gap-1.5 px-5 py-2 rounded-xl text-sm font-bold text-white shadow transition
                  ${primaryBtnClass} ${primaryHoverClass} disabled:opacity-50`}>
                {submitting ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {submitting ? "Saving..." : "Submit"}
              </button>
            </div>
          </div>

          {/* ── Header Form ── */}
          <div className={`${cardBase} p-5 mb-5`}>
            {sectionTitle("Header Information")}

            {/* Row 1: Branch + GRN Conf No + GRN No */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className={labelBase}>Branch</label>
                <div className="flex gap-4 mt-1.5">
                  <label className="flex items-center gap-1.5 text-xs">
                    <input type="radio" checked={branchMode === "HO"} readOnly disabled /> H.O.
                  </label>
                  <label className="flex items-center gap-1.5 text-xs">
                    <input type="radio" checked={branchMode === "BRANCH"} readOnly disabled /> BRANCH
                  </label>
                </div>
              </div>
              {showConfirmation && (
                <div>
                  <label className={labelBase}>Confirmation</label>
                  <div className="mt-1.5">
                    <input type="checkbox" checked={confirmation} onChange={(e) => setConfirmation(e.target.checked)} />
                  </div>
                </div>
              )}
              <div>
                <label className={labelBase}>GRN Conf No</label>
                <div className={`mt-1 text-sm font-bold ${isDark ? "text-purple-300" : "text-purple-700"}`}>
                  {grnConfNo || "—"}
                </div>
              </div>
              <div>
                <label className={labelBase}>GRN No</label>
                <div className={`mt-1 text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  {grnNo || "—"}
                </div>
              </div>
            </div>

            {/* Row 2: GRN Date + Time + GRN Ins No */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className={labelBase}>GRN Date <span className="text-red-500">*</span></label>
                <input type="date" value={grnDate}
                  onChange={(e) => handleGrnDateChange(e.target.value)}
                  className={inputBase + " mt-1"} />
              </div>
              {siteInventory && (
                <div>
                  <label className={labelBase}>Time <span className="text-red-500">*</span></label>
                  <input type="text" value={grnTime}
                    onChange={(e) => setGrnTime(e.target.value)}
                    placeholder="hh:mm AM/PM"
                    className={inputBase + " mt-1"} />
                </div>
              )}
              <div>
                <label className={labelBase}>GRN Inspection No.</label>
                <div className={`mt-1 text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  {grnInsNo || "—"}
                </div>
              </div>
            </div>

            {/* Row 3: Ref Date + Supplier */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className={labelBase}>Ref Date</label>
                <input type="date" value={refDate}
                  onChange={(e) => setRefDate(e.target.value)}
                  className={inputBase + " mt-1"} />
              </div>
              <div className="md:col-span-3">
                <label className={labelBase}>Supplier</label>
                <div className={`mt-1 text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  {supplier || "—"}
                </div>
              </div>
            </div>

            {/* Row 4: Invoice No + Invoice Date */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className={labelBase}>Invoice No <span className="text-red-500">*</span></label>
                <input type="text" value={invoiceNo}
                  onChange={(e) => setInvoiceNo(e.target.value)}
                  onBlur={handleInvoiceNoBlur}
                  className={inputBase + " mt-1"} />
              </div>
              <div>
                <label className={labelBase}>Invoice Date <span className="text-red-500">*</span></label>
                <input type="date" value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  className={inputBase + " mt-1"} />
              </div>
            </div>

            {/* Row 5: Invoice Copy + Ref No + Remarks */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div className="md:col-span-2">
                <label className={labelBase}>Invoice Copy</label>
                <div className="flex gap-2 mt-1">
                  <input ref={fileInputRef} type="file" className={inputBase + " text-xs"} />
                  <button onClick={handleFileAdd}
                    className={`shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-xl text-white text-xs font-bold ${primaryBtnClass} ${primaryHoverClass}`}>
                    <Upload className="w-3 h-3" /> Add
                  </button>
                </div>
                {invoiceFiles.length > 0 && (
                  <div className={`mt-2 rounded-xl border overflow-hidden ${isDark ? "border-white/10" : "border-gray-200"}`}>
                    {invoiceFiles.map((f, i) => (
                      <div key={i}
                        onClick={() => setSelectedFileIdx(i === selectedFileIdx ? null : i)}
                        className={`flex items-center justify-between px-3 py-1.5 text-xs cursor-pointer
                          ${i === selectedFileIdx ? (isDark ? "bg-blue-900/40" : "bg-blue-50") : (isDark ? "bg-gray-800" : "bg-gray-50")}
                          ${i > 0 ? "border-t " + (isDark ? "border-white/10" : "border-gray-200") : ""}`}>
                        <span className="flex items-center gap-1.5"><FileText className="w-3 h-3" />{f}</span>
                        <button onClick={(e) => { e.stopPropagation(); setInvoiceFiles((prev) => prev.filter((_, j) => j !== i)); }}>
                          <Trash2 className="w-3 h-3 text-red-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className={labelBase}>Ref No</label>
                <textarea value={refNo} readOnly rows={3} className={inputBase + " mt-1 resize-none opacity-70"} />
              </div>
              <div>
                <label className={labelBase}>Remarks <span className="text-red-500">*</span></label>
                <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={3} className={inputBase + " mt-1 resize-none"} />
              </div>
            </div>

            {/* Row 6: Employee + Delivery Type */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className={labelBase}>Employee</label>
                <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} className={inputBase + " mt-1"}>
                  <option value="Select">Select</option>
                  {employees.map((emp) => (
                    <option key={emp.employeeid} value={String(emp.employeeid)}>{emp.employeename}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelBase}>Delivery Type</label>
                <input type="text" value={deliveryType} onChange={(e) => setDeliveryType(e.target.value)} className={inputBase + " mt-1"} />
              </div>
            </div>
          </div>

          {/* ── Grid 1: Purchase Orders ── */}
          <div className={`${cardBase} mb-5`}>
            <div className={`px-4 py-3 border-b ${isDark ? "border-white/10" : "border-gray-200"}`}>
              {sectionTitle("Purchase Orders")}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    {["Sl No", "Purchase Order No.", "Date", "Supplier", "Due Date", "Status", "Check"].map((h) => (
                      <th key={h} className={thBase}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? "divide-white/5" : "divide-gray-100"}`}>
                  {loading ? (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-xs text-gray-400">Loading...</td></tr>
                  ) : poPagedRows.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-xs text-gray-400">No Records Found</td></tr>
                  ) : poPagedRows.map((row, idx) => (
                    <tr key={idx} className={isDark ? "hover:bg-white/5" : "hover:bg-gray-50"}>
                      <td className={tdBase + " text-center"}>{(poPage - 1) * PO_PAGE_SIZE + idx + 1}</td>
                      <td className={tdBase}>{row.fld_pono || row.fld_POno || row.FLD_PONo || ""}</td>
                      <td className={tdBase}>{row.fld_podate || row.fld_Podate || ""}</td>
                      <td className={tdBase}>{row.fld_suppliername || ""}</td>
                      <td className={tdBase}>{row.fld_duedate || ""}</td>
                      <td className={tdBase}>{row.Status || row.status || ""}</td>
                      <td className="px-3 py-2 text-center">
                        <input type="checkbox" checked disabled className="w-3.5 h-3.5 opacity-60" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-3">
              <Pagination page={poPage} totalPages={poTotalPages} totalCount={purchaseOrders.length}
                pageSize={PO_PAGE_SIZE} onPageChange={setPoPage} isDark={isDark} primaryBtnClass={primaryBtnClass} />
            </div>
          </div>

          {/* ── Info message ── */}
          {infoMessage && (
            <div className={`flex items-start gap-2 p-3 mb-4 rounded-xl text-xs font-medium
              ${isDark ? "bg-yellow-900/30 text-yellow-200 border border-yellow-700/40"
                       : "bg-yellow-50 text-yellow-800 border border-yellow-200"}`}>
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              {infoMessage}
            </div>
          )}

          {/* ── Grid 2: Items ── */}
          <div className={`${cardBase} mb-5`}>
            <div className={`px-4 py-3 border-b ${isDark ? "border-white/10" : "border-gray-200"}`}>
              {sectionTitle("Items")}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr>
                    {["Sl No","Item Code","Item Name","HSN No","UOM","Order Qty","GRN Qty","Accepted","Remarks","Unit Price","DS%","CGST%","SGST%","IGST%","Check"].map((h) => (
                      <th key={h} className={thBase + " whitespace-nowrap"}>{h}</th>
                    ))}
                    {branchType === "HO" && isUpdateMode && (
                      <>
                        <th className={thBase}>VAT Approve</th>
                        <th className={thBase}>VAT DisApprove</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? "divide-white/5" : "divide-gray-100"}`}>
                  {loading ? (
                    <tr><td colSpan={17} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
                  ) : displayItems.length === 0 ? (
                    <tr><td colSpan={17} className="px-4 py-8 text-center text-gray-400">No Records Found</td></tr>
                  ) : displayItems.map((item, idx) => {
                    const globalIdx = (itemPage - 1) * ITEM_PAGE_SIZE + idx;
                    return (
                      <tr key={globalIdx} className={isDark ? "hover:bg-white/5" : "hover:bg-gray-50"}>
                        <td className={tdBase + " text-center"}>{globalIdx + 1}</td>
                        <td className={tdBase}>{item.fld_itemcode}</td>
                        <td className={tdBase + " min-w-[140px]"}>{item.fld_itemname}</td>
                        <td className={tdBase}>{item.fld_itemHSNNo}</td>
                        <td className={tdBase}>{item.fld_uomname}</td>
                        <td className={tdBase + " text-right"}>{item.fld_POqty}</td>
                        <td className={tdBase + " text-right"}>{item.fld_grnqty}</td>
                        <td className={tdBase + " text-right"}>{item.fld_acceptedqty}</td>
                        <td className={tdBase}>{item.fld_remarks}</td>
                        <td className={tdBase + " text-right"}>{item.fld_poprice}</td>
                        <td className={tdBase + " text-right"}>{item.fld_DS}</td>
                        <td className={tdBase + " text-right"}>{item.fld_CST}</td>
                        <td className={tdBase + " text-right"}>{item.fld_VAT}</td>
                        <td className={tdBase + " text-right"}>{item.fld_ED}</td>
                        <td className="px-3 py-2 text-center">
                          <input type="checkbox" checked={!!item._checked}
                            onChange={(e) => toggleItemCheck(globalIdx, e.target.checked)}
                            className="w-3.5 h-3.5" />
                        </td>
                        {branchType === "HO" && isUpdateMode && (
                          <>
                            <td className="px-3 py-2 text-center">
                              <input type="checkbox"
                                checked={item._vatApprove === 1}
                                onChange={(e) => setAllItems((prev) => prev.map((it, i) =>
                                  i === globalIdx ? { ...it, _vatApprove: e.target.checked ? 1 : null } : it
                                ))}
                                className="w-3.5 h-3.5" />
                            </td>
                            <td className="px-3 py-2 text-center">
                              <input type="checkbox"
                                checked={item._vatApprove === 0}
                                onChange={(e) => setAllItems((prev) => prev.map((it, i) =>
                                  i === globalIdx ? { ...it, _vatApprove: e.target.checked ? 0 : null } : it
                                ))}
                                className="w-3.5 h-3.5" />
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="p-3">
              <Pagination page={itemPage} totalPages={itemTotalPages} totalCount={allItems.length}
                pageSize={ITEM_PAGE_SIZE} onPageChange={setItemPage} isDark={isDark} primaryBtnClass={primaryBtnClass} />
            </div>
          </div>

          {/* ── Summary / Totals ── */}
          {totals && (
            <div className={`${cardBase} p-5`}>
              {sectionTitle("Summary")}
              <div className="max-w-sm ml-auto space-y-1">
                {summaryRow("Sub Total",     totals.subtotal)}
                {summaryRow("Discount",      totals.discount)}
                {summaryRow("A.Dis Total",   totals.afDisTotal)}
                {summaryRow("Tax Value",     totals.taxValue)}
                {summaryRow("Freight",       totals.frieght)}
                {summaryRow("Insurance",     totals.insurance)}
                {summaryRow("PF",            totals.pf)}
                {summaryRow("CGST",          totals.cgst)}
                {summaryRow("SGST",          totals.sgst)}
                {summaryRow("IGST",          totals.igst)}
                {summaryRow(`TCS (${totals.tcsper}%)`, totals.tcsamt)}
                {summaryRow("Round Off",     totals.roundOff)}
                {summaryRow("Other Charges", totals.other)}
                <div className={`border-t mt-2 pt-2 ${isDark ? "border-white/10" : "border-gray-200"}`}>
                  {summaryRow("Total Amount", totals.totalAmt, true)}
                </div>
              </div>
              <div className="flex justify-end mt-5">
                <button onClick={handleSubmit} disabled={submitting || loading}
                  className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold text-white shadow transition
                    ${primaryBtnClass} ${primaryHoverClass} disabled:opacity-50`}>
                  {submitting ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  {submitting ? "Saving..." : "Submit"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </LayoutWrapper>
  );
};

export default GoodsReceiptNoteConfirmEntryGST;